import React, { useEffect } from 'react';
import classNames from 'classnames';

import css from './CountryFlag.module.css';

// Load flag-icons CSS dynamically to avoid render-blocking
let flagIconsLoaded = false;
const loadFlagIconsCSS = () => {
  if (flagIconsLoaded || typeof document === 'undefined') return;

  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = 'https://cdn.jsdelivr.net/npm/flag-icons@7.2.3/css/flag-icons.min.css';
  link.media = 'print';
  link.onload = () => { link.media = 'all'; };
  document.head.appendChild(link);
  flagIconsLoaded = true;
};

/**
 * CountryFlag component displays a country flag emoji with optional country name
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {Object} props.flagData - Flag data object
 * @param {string} props.flagData.flag - Flag emoji
 * @param {string} props.flagData.countryCode - ISO country code
 * @param {string} props.flagData.countryName - Country name
 * @param {string} [props.flagData.currency] - Currency code
 * @param {boolean} [props.showCountryName=false] - Whether to show country name alongside flag
 * @param {boolean} [props.showTooltip=true] - Whether to show tooltip with country info
 * @param {'small'|'medium'|'large'|'xlarge'} [props.size='medium'] - Size of the flag
 * @returns {JSX.Element} CountryFlag component
 */
const CountryFlag = props => {
  const {
    className,
    rootClassName,
    flagData,
    showCountryName = false,
    showTooltip = true,
    size = 'medium',
  } = props;

  // Load flag-icons CSS when component mounts
  useEffect(() => {
    loadFlagIconsCSS();
  }, []);

  if (!flagData || !flagData.flag) {
    return null;
  }

  const { flag, countryCode, countryName, currency } = flagData;

  const classes = classNames(rootClassName || css.root, css[size], className);

  const flagClasses = classNames(
    css.flag,
    css[`flag${size.charAt(0).toUpperCase()}${size.slice(1)}`]
  );

  // Create tooltip content
  const tooltipContent = showTooltip
    ? `${countryName || countryCode}${currency ? ` (${currency})` : ''}`
    : null;

  return (
    <span
      className={classes}
      title={tooltipContent}
      aria-label={`Country: ${countryName || countryCode}`}
    >
      <span
        className={`${flagClasses} ${css.rectangularFlag} fi fi-${countryCode.toLowerCase()}`}
        role="img"
        aria-label={`${countryName || countryCode} flag`}
      ></span>
      {showCountryName && countryName && <span className={css.countryName}>{countryName}</span>}
    </span>
  );
};

export default CountryFlag;
